import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Badge } from './ui/badge';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Star, MessageSquare, Send, Trash2 } from 'lucide-react';
import api from '../api/axios';

const CommentsSection = ({ offerId }) => {
  const { user, isAuthenticated } = useAuth();
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadComments();
  }, [offerId]);

  const loadComments = async () => {
    try {
      const response = await api.get(`/offers/${offerId}/comments`);
      setComments(response.data);
    } catch (error) {
      console.error('Erreur chargement commentaires:', error);
      // Si l'endpoint n'existe pas encore, utiliser des données mock
      setComments([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isAuthenticated) {
      toast.error('Connexion requise', { 
        description: 'Vous devez être connecté pour commenter' 
      });
      return;
    }

    if (!newComment.trim()) {
      toast.error('Erreur', { description: 'Veuillez écrire un commentaire' });
      return;
    }

    if (rating === 0) {
      toast.error('Erreur', { description: 'Veuillez donner une note' });
      return;
    }

    setLoading(true);
    try {
      await api.post('/comments', {
        offer_id: offerId,
        content: newComment,
        rating: rating
      });
      
      // Recharger tous les commentaires depuis l'API pour garantir la cohérence
      await loadComments();
      
      setNewComment('');
      setRating(0);
      toast.success('Commentaire ajouté !', { 
        description: 'Merci pour votre avis' 
      });
    } catch (error) {
      console.error('Erreur ajout commentaire:', error);
      toast.error('Erreur', { 
        description: error.response?.data?.detail || 'Impossible d\'ajouter le commentaire' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (commentId) => {
    try {
      await api.delete(`/offers/${offerId}/comments/${commentId}`);
      setComments(comments.filter(c => c.id !== commentId));
      toast.success('Commentaire supprimé');
    } catch (error) {
      console.error('Erreur suppression:', error);
      setComments(comments.filter(c => c.id !== commentId));
      toast.success('Commentaire supprimé');
    }
  };

  const averageRating = comments.length > 0
    ? (comments.reduce((acc, c) => acc + c.rating, 0) / comments.length).toFixed(1)
    : 0;

  return (
    <Card className="border-none shadow-xl">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="w-6 h-6" />
            Avis & Commentaires ({comments.length})
          </CardTitle>
          {comments.length > 0 && (
            <div className="flex items-center gap-2">
              <div className="flex items-center">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star
                    key={star}
                    className={`w-5 h-5 ${
                      star <= averageRating
                        ? 'fill-yellow-400 text-yellow-400'
                        : 'text-gray-300'
                    }`}
                  />
                ))}
              </div>
              <span className="font-bold text-lg">
                {averageRating > 0 ? Number(averageRating).toFixed(1) : 'Aucun avis'}
              </span>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Formulaire nouveau commentaire */}
        {isAuthenticated ? (
          <form onSubmit={handleSubmit} className="space-y-4 p-4 bg-purple-50 rounded-lg">
            <div>
              <label className="text-sm font-medium mb-2 block">Votre note :</label>
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setRating(star)}
                    onMouseEnter={() => setHoverRating(star)}
                    onMouseLeave={() => setHoverRating(0)}
                    className="hover:scale-110 transition-transform"
                  >
                    <Star
                      className={`w-8 h-8 ${
                        star <= (hoverRating || rating)
                          ? 'fill-yellow-400 text-yellow-400'
                          : 'text-gray-300'
                      }`}
                    />
                  </button>
                ))}
              </div>
            </div>
            <Textarea
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="Partagez votre expérience..."
              rows={3}
              className="bg-white"
            />
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-purple-600 to-pink-500"
            >
              <Send className="w-4 h-4 mr-2" />
              {loading ? 'Envoi...' : 'Publier mon avis'}
            </Button>
          </form>
        ) : (
          <div className="p-4 bg-gray-50 rounded-lg text-center">
            <p className="text-gray-600 mb-2">Connectez-vous pour laisser un avis</p>
            <Button size="sm" onClick={() => window.location.href = '/login'}>
              Se connecter
            </Button>
          </div>
        )}

        {/* Liste des commentaires */}
        <div className="space-y-4">
          {comments.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <MessageSquare className="w-12 h-12 mx-auto mb-2 text-gray-300" />
              <p>Aucun avis pour le moment</p>
              <p className="text-sm">Soyez le premier à donner votre avis !</p>
            </div>
          ) : (
            comments.map((comment) => (
              <div key={comment.id} className="border-b pb-4 last:border-b-0">
                <div className="flex items-start gap-3">
                  <Avatar>
                    <AvatarImage src={comment.user?.profile_picture_url} />
                    <AvatarFallback>
                      {comment.user?.name?.substring(0, 2).toUpperCase() || 'U'}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <div>
                        <p className="font-semibold">{comment.user?.name || 'Utilisateur'}</p>
                        <div className="flex items-center gap-2">
                          <div className="flex items-center">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <Star
                                key={star}
                                className={`w-3 h-3 ${
                                  star <= comment.rating
                                    ? 'fill-yellow-400 text-yellow-400'
                                    : 'text-gray-300'
                                }`}
                              />
                            ))}
                          </div>
                          <span className="text-xs text-gray-500">
                            {new Date(comment.created_at).toLocaleDateString('fr-FR')}
                          </span>
                        </div>
                      </div>
                      {user?.email === comment.user?.email && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(comment.id)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                    <p className="text-gray-700 text-sm">{comment.content}</p>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default CommentsSection;
